import { useState, useCallback } from "react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Loader2, Wand2, ChevronDown, ChevronRight, BarChart3, AlertCircle } from "lucide-react"
import { ActionMeasureTree } from "./ActionMeasureTree"
import { TabsContent } from "./ui/tabs"
import {
  useClassifyMeasuresMutation,
  useUpdateActionMeasuresMutation,
  useGetAllMeasuresQuery,
  type ActionCategory,
  type ClassifyMeasuresRequest,
  type GenerationOptions
} from "../app/services/simulationCreatorApi"

type MeasureMappingStepProps = {
  actions: ActionCategory[]
  apiKeys: Record<string, string>
  settings: GenerationOptions
  simulationId: string
  onActionsUpdate: (actions: ActionCategory[]) => void
  onPrevious: () => void
  onNext: () => void
}

// "reasoning" is a UI-only value that gets mapped to openai-reasoning or claude-reasoning before sending
type ClassificationMode = ClassifyMeasuresRequest["classificationMode"] | "reasoning"

// Helper to count measures in action tree
const countMeasures = (actions: ActionCategory[]): { leafActions: number; measuresAssigned: number } => {
  let leafActions = 0
  let measuresAssigned = 0

  const traverse = (items: ActionCategory[]) => {
    for (const action of items) {
      if (!action.categories || action.categories.length === 0) {
        leafActions++
        measuresAssigned += action.measureIds?.length || 0
      } else {
        traverse(action.categories)
      }
    }
  }

  traverse(actions)
  return { leafActions, measuresAssigned }
}

// Helper to check if any measures are assigned
const hasMeasures = (actions: ActionCategory[]): boolean => {
  for (const action of actions) {
    if (action.measureIds && action.measureIds.length > 0) {
      return true
    }
    if (action.categories && action.categories.length > 0) {
      if (hasMeasures(action.categories)) {
        return true
      }
    }
  }
  return false
}

export const MeasureMappingStep = ({
  actions,
  apiKeys,
  settings,
  simulationId,
  onActionsUpdate,
  onPrevious,
  onNext
}: MeasureMappingStepProps) => {
  const [classificationMode, setClassificationMode] = useState<ClassificationMode>("reasoning")
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [lastClassificationTime, setLastClassificationTime] = useState<number | null>(null)

  // API hooks
  const { data: measuresData, isLoading: isLoadingMeasures } = useGetAllMeasuresQuery()
  const [classifyMeasures, { isLoading: isClassifying, error: classifyError }] = useClassifyMeasuresMutation()
  const [updateActionMeasures] = useUpdateActionMeasuresMutation()

  const measures = measuresData?.data || []
  const stats = countMeasures(actions)
  const hasExistingMeasures = hasMeasures(actions)
  const selectedReasoningProvider = settings.aiProvider || "openai"
  const selectedReasoningModel = settings.aiModel?.trim() || undefined

  // Handle classification
  const handleClassify = async () => {
    const keyProviderByMode: Record<ClassificationMode, string> = {
      embedding: "openai",
      reasoning: selectedReasoningProvider,
      "openai-reasoning": "openai",
      "claude-reasoning": "anthropic",
    }
    const apiKey = apiKeys[keyProviderByMode[classificationMode]]?.trim() || undefined

    // Map generic "reasoning" to provider-specific mode the API expects
    const resolvedMode: ClassifyMeasuresRequest["classificationMode"] =
      classificationMode === "reasoning"
        ? (selectedReasoningProvider === "anthropic" ? "claude-reasoning" : "openai-reasoning")
        : classificationMode

    const request: ClassifyMeasuresRequest = {
      classificationMode: resolvedMode,
      forceReclassify: hasExistingMeasures,
      apiKey,
    }

    try {
      const result = await classifyMeasures({
        simulationId,
        request
      }).unwrap()

      if (result.success) {
        onActionsUpdate(result.actions)
        setLastClassificationTime(result.metadata.processingTimeMs)
      }
    } catch (error) {
      console.error("Classification failed:", error)
    }
  }

  // Handle adding a measure
  const handleAddMeasure = useCallback(async (actionPath: string, measureId: number) => {
    // Get current measures for the action
    const getActionMeasures = (items: ActionCategory[], path: string): number[] => {
      const parts = path.split(".")
      let current: ActionCategory[] | ActionCategory = items

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        if (part === "categories") continue

        const index = parseInt(part, 10)
        if (Array.isArray(current)) {
          current = current[index]
        } else if (current.categories) {
          current = current.categories[index]
        }
      }

      return Array.isArray(current) ? [] : (current?.measureIds || [])
    }

    const currentMeasures = getActionMeasures(actions, actionPath)
    const newMeasures = [...currentMeasures, measureId]

    try {
      await updateActionMeasures({
        simulationId,
        request: {
          updates: [{ actionPath, measureIds: newMeasures }]
        }
      }).unwrap()

      // Update local state optimistically
      const updateActions = (items: ActionCategory[], path: string, newIds: number[]): ActionCategory[] => {
        return items.map((item, index) => {
          const currentPathPrefix = `${index}`
          if (path === currentPathPrefix) {
            return { ...item, measureIds: newIds }
          }
          if (path.startsWith(`${currentPathPrefix}.categories.`) && item.categories) {
            const remainingPath = path.slice(`${currentPathPrefix}.categories.`.length)
            return {
              ...item,
              categories: updateActions(item.categories, remainingPath, newIds)
            }
          }
          return item
        })
      }

      onActionsUpdate(updateActions(actions, actionPath, newMeasures))
    } catch (error) {
      console.error("Failed to add measure:", error)
    }
  }, [actions, simulationId, updateActionMeasures, onActionsUpdate])

  // Handle removing a measure
  const handleRemoveMeasure = useCallback(async (actionPath: string, measureId: number) => {
    // Get current measures and remove the specified one
    const getActionMeasures = (items: ActionCategory[], path: string): number[] => {
      const parts = path.split(".")
      let current: ActionCategory[] | ActionCategory = items

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        if (part === "categories") continue

        const index = parseInt(part, 10)
        if (Array.isArray(current)) {
          current = current[index]
        } else if (current.categories) {
          current = current.categories[index]
        }
      }

      return Array.isArray(current) ? [] : (current?.measureIds || [])
    }

    const currentMeasures = getActionMeasures(actions, actionPath)
    const newMeasures = currentMeasures.filter(id => id !== measureId)

    try {
      await updateActionMeasures({
        simulationId,
        request: {
          updates: [{ actionPath, measureIds: newMeasures }]
        }
      }).unwrap()

      // Update local state optimistically
      const updateActions = (items: ActionCategory[], path: string, newIds: number[]): ActionCategory[] => {
        return items.map((item, index) => {
          const currentPathPrefix = `${index}`
          if (path === currentPathPrefix) {
            return { ...item, measureIds: newIds }
          }
          if (path.startsWith(`${currentPathPrefix}.categories.`) && item.categories) {
            const remainingPath = path.slice(`${currentPathPrefix}.categories.`.length)
            return {
              ...item,
              categories: updateActions(item.categories, remainingPath, newIds)
            }
          }
          return item
        })
      }

      onActionsUpdate(updateActions(actions, actionPath, newMeasures))
    } catch (error) {
      console.error("Failed to remove measure:", error)
    }
  }, [actions, simulationId, updateActionMeasures, onActionsUpdate])

  if (!actions || actions.length === 0) {
    return (
      <TabsContent value="measure-mapping">
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No actions available. Generate actions first before mapping measures.</p>
              <Button variant="outline" onClick={onPrevious} className="mt-4">
                Go to Actions Step
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    )
  }

  return (
    <TabsContent value="measure-mapping">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Classification Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Auto-Classification
            </CardTitle>
            <CardDescription>
              Automatically assign organizational measures to actions using AI
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">
                  {hasExistingMeasures
                    ? `${stats.measuresAssigned} measures assigned to ${stats.leafActions} actions. Click to reclassify.`
                    : `${stats.leafActions} actions ready to classify.`}
                </p>
                {lastClassificationTime && (
                  <p className="text-xs text-gray-500 mt-1">
                    Last classification took {(lastClassificationTime / 1000).toFixed(1)}s
                  </p>
                )}
              </div>
              <Button
                onClick={handleClassify}
                disabled={isClassifying || isLoadingMeasures}
              >
                {isClassifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Classifying...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    {hasExistingMeasures ? "Reclassify Actions" : "Classify Actions"}
                  </>
                )}
              </Button>
            </div>

            {classifyError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-4">
                Classification failed. Please try again.
              </div>
            )}

            {/* Advanced Options */}
            <div className="border-t pt-4">
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                {showAdvancedOptions ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Advanced Options
              </button>

              {showAdvancedOptions && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">
                    Classification Method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="classificationMode"
                        value="embedding"
                        checked={classificationMode === "embedding"}
                        onChange={() => setClassificationMode("embedding")}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">Fast (Embedding-based)</div>
                        <div className="text-xs text-gray-500">
                          Quick semantic matching using text embeddings. Best for speed.
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="classificationMode"
                        value="reasoning"
                        checked={classificationMode === "reasoning"}
                        onChange={() => setClassificationMode("reasoning")}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">
                          Reasoning ({selectedReasoningProvider}{selectedReasoningModel ? ` - ${selectedReasoningModel}` : ""})
                        </div>
                        <div className="text-xs text-gray-500">
                          Contextual classification using the current AI provider and model from Settings.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Measures Editor */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Action Measures
                  <Badge variant="secondary">
                    {stats.measuresAssigned} assigned
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Review and edit measure assignments for each action
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingMeasures ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading measures...</span>
              </div>
            ) : (
              <div className="border rounded-lg p-4 max-h-[500px] overflow-y-auto">
                <ActionMeasureTree
                  actions={actions}
                  measures={measures}
                  onAddMeasure={handleAddMeasure}
                  onRemoveMeasure={handleRemoveMeasure}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          <Button onClick={onNext}>
            Complete Simulation
          </Button>
        </div>
      </div>
    </TabsContent>
  )
}

export default MeasureMappingStep
